
import React from 'react';

interface FilterSidebarProps {
  priceRange: [number, number];
  setPriceRange: (range: [number, number]) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  categories: string[];
}

export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  priceRange,
  setPriceRange,
  selectedCategory,
  setSelectedCategory,
  categories
}) => {
  const handlePriceChange = (index: number, value: string) => {
    const numValue = parseInt(value) || 0;
    const newRange: [number, number] = [...priceRange];
    newRange[index] = numValue;
    setPriceRange(newRange);
  };

  const clearFilters = () => {
    setPriceRange([0, 100000]);
    setSelectedCategory('');
  };

  return (
    <div className="w-80 bg-white rounded-lg shadow-lg p-6 h-fit">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Filter by</h3>
        <button
          onClick={clearFilters}
          className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
        >
          Clear All
        </button>
      </div>

      {/* Price Range Filter */}
      <div className="mb-8">
        <h4 className="font-medium text-gray-900 mb-4">Price Range</h4>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">Min Price</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                <input
                  type="number"
                  value={priceRange[0]}
                  onChange={(e) => handlePriceChange(0, e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="0"
                />
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">Max Price</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                <input
                  type="number"
                  value={priceRange[1]}
                  onChange={(e) => handlePriceChange(1, e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="100000"
                />
              </div>
            </div>
          </div>
          
          {/* Quick Price Filters */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Under ₹5K', range: [0, 5000] },
              { label: '₹5K - ₹10K', range: [5000, 10000] },
              { label: '₹10K - ₹20K', range: [10000, 20000] },
              { label: 'Above ₹20K', range: [20000, 100000] }
            ].map((option) => (
              <button
                key={option.label}
                onClick={() => setPriceRange(option.range as [number, number])}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors duration-200 ${
                  priceRange[0] === option.range[0] && priceRange[1] === option.range[1]
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-500'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="mb-8">
        <h4 className="font-medium text-gray-900 mb-4">Category</h4>
        <div className="space-y-2">
          <button
            onClick={() => setSelectedCategory('')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-colors duration-200 ${
              selectedCategory === ''
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            All Categories
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors duration-200 ${
                selectedCategory === category
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Popular Destinations */}
      <div>
        <h4 className="font-medium text-gray-900 mb-4">Popular Destinations</h4>
        <div className="space-y-2">
          {['Scotland', 'London', 'India', 'New York', 'Ireland'].map((destination) => (
            <div key={destination} className="flex items-center justify-between text-sm">
              <span className="text-gray-700">{destination}</span>
              <span className="text-gray-500">
                {destination === 'Scotland' ? '3' : destination === 'India' ? '4' : '2'} tours
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
